import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi, attachmentsApi, customersApi } from '../../lib/api-hooks';
import { PRIORITET_LABELS } from '../../lib/format';
import type { Prioritet, Customer } from '../../lib/types';
import { clsx } from 'clsx';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface SelectedCustomer {
  id: string;
  navn: string | null;
  epost: string;
  firma: string | null;
}

export function NewTicketModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [tittel, setTittel] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [prioritet, setPrioritet] = useState<Prioritet>('NORMAL');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Autocomplete-state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  // Søk kunder med debounce
  const { data: searchResults } = useQuery({
    queryKey: ['customer-search', searchQuery],
    queryFn: () => customersApi.list(searchQuery),
    enabled: searchQuery.length >= 2,
    staleTime: 10_000,
  });

  // Debounce søk når e-post/navn endres
  useEffect(() => {
    if (selectedCustomer) return; // Ikke søk når kunde allerede valgt
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(customerEmail || customerName);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [customerEmail, customerName, selectedCustomer]);

  const suggestions = (searchResults ?? []).slice(0, 6);

  const selectCustomer = (c: Customer & { firma?: string | null }) => {
    setSelectedCustomer({ id: c.id, navn: c.navn ?? null, epost: c.epost, firma: c.firma ?? null });
    setCustomerEmail(c.epost);
    setCustomerName(c.navn ?? '');
    setShowSuggestions(false);
    setHighlightedIdx(-1);
  };

  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerEmail('');
    setCustomerName('');
    setShowSuggestions(true);
    setTimeout(() => emailInputRef.current?.focus(), 0);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0) {
      e.preventDefault();
      selectCustomer(suggestions[highlightedIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setHighlightedIdx(-1);
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      // 1. Opprett sak — bruk customerId hvis eksisterende kunde valgt
      const ticket = await ticketsApi.create({
        tittel,
        beskrivelse,
        prioritet,
        customerId: selectedCustomer?.id,
        customerEmail: selectedCustomer ? undefined : (customerEmail || undefined),
        customerName: selectedCustomer ? undefined : (customerName || undefined),
      });

      // 2. Last opp vedlegg
      for (const file of files) {
        await attachmentsApi.upload(ticket.id, file);
      }

      return ticket;
    },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      // Reset
      setTittel('');
      setBeskrivelse('');
      setPrioritet('NORMAL');
      setCustomerEmail('');
      setCustomerName('');
      setFiles([]);
      setUploadError(null);
      setSelectedCustomer(null);
      setSearchQuery('');
      setShowSuggestions(false);
      onClose();
      // Naviger til saken
      window.location.href = `/ticket/${ticket.id}`;
    },
  });

  if (!open) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    setUploadError(null);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tittel.trim()) return;
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="card max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Ny sak</h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Kunde-info med autocomplete */}
          {selectedCustomer ? (
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-3 dark:border-brand-500/30 dark:bg-brand-500/10">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span className="text-sm font-semibold text-brand-700 dark:text-brand-300">Eksisterende kunde</span>
                  </div>
                  <p className="mt-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {selectedCustomer.navn || selectedCustomer.epost}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {selectedCustomer.epost}
                    {selectedCustomer.firma && ` · ${selectedCustomer.firma}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearSelectedCustomer}
                  className="btn-ghost shrink-0 p-1 text-slate-400 hover:text-red-600"
                  title="Fjern kunde"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label className="mb-1 block text-sm font-medium text-slate-700">Kunde e-post</label>
                <input
                  ref={emailInputRef}
                  type="email"
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value);
                    setShowSuggestions(true);
                    setHighlightedIdx(-1);
                  }}
                  onKeyDown={handleEmailKeyDown}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="kunde@eksempel.no"
                  className="input"
                  autoComplete="off"
                />
                {/* Autocomplete dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
                    <div className="px-2 py-1.5 text-xs font-medium text-slate-400">
                      Eksisterende kunder
                    </div>
                    {suggestions.map((c, i) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); selectCustomer(c); }}
                        onMouseEnter={() => setHighlightedIdx(i)}
                        className={clsx(
                          'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors',
                          highlightedIdx === i
                            ? 'bg-brand-50 dark:bg-brand-500/10'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                          {(c.navn || c.epost).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                            {c.navn || c.epost}
                          </p>
                          <p className="truncate text-xs text-slate-400">
                            {c.epost}
                            {c.firma && ` · ${c.firma}`}
                          </p>
                        </div>
                        {c._count && (
                          <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                            {c._count.tickets} saker
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Kunde navn (valgfritt)</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => {
                    setCustomerName(e.target.value);
                    setShowSuggestions(true);
                    setHighlightedIdx(-1);
                  }}
                  onKeyDown={handleEmailKeyDown}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Kari Nordmann"
                  className="input"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {/* Tittel */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Tittel *</label>
            <input
              type="text"
              required
              value={tittel}
              onChange={(e) => setTittel(e.target.value)}
              placeholder="Kort beskrivelse av saken"
              className="input"
              autoFocus
            />
          </div>

          {/* Beskrivelse */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Beskrivelse</label>
            <textarea
              value={beskrivelse}
              onChange={(e) => setBeskrivelse(e.target.value)}
              placeholder="Detaljert beskrivelse av saken…"
              rows={4}
              className="input resize-y"
            />
          </div>

          {/* Prioritet */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Prioritet</label>
            <div className="flex gap-1">
              {(['LAV', 'NORMAL', 'HOY', 'KRITISK'] as Prioritet[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPrioritet(p)}
                  className={clsx(
                    'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                    prioritet === p
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                  )}
                >
                  {PRIORITET_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          {/* Vedlegg */}
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Vedlegg</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 p-4 text-center transition-colors hover:border-brand-400 hover:bg-brand-50"
            >
              <svg className="mx-auto mb-1 h-8 w-8 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <p className="text-sm text-slate-500">Klikk for å velge filer</p>
              <p className="text-xs text-slate-400">Maks 10 MB per fil</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {/* Valgte filer */}
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5">
                    <div className="flex items-center gap-2 truncate">
                      <svg className="h-4 w-4 shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      <span className="truncate text-sm text-slate-700">{file.name}</span>
                      <span className="shrink-0 text-xs text-slate-400">
                        ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Feilmelding */}
          {(createMutation.error || uploadError) && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {(createMutation.error as Error)?.message || uploadError}
            </div>
          )}

          {/* Knapper */}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Avbryt
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createMutation.isPending || !tittel.trim()}
            >
              {createMutation.isPending ? 'Oppretter…' : 'Opprett sak'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
